import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

export const MODAL_ID = "gestaoAcessosV2EditarGrupo";
const LIST_PERMISSOES_URL = "/Painel/API/AccessManagement/GestaoAcessos/ListPermissoesGrupo/";
const SET_PERMISSAO_URL = "/Painel/API/AccessManagement/GestaoAcessos/SetPermissaoGrupo/";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitulo = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
`;

const Lista = styled.div`
    width: 100%;
    max-height: 450px;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-sizing: border-box;
`;

const PermissaoItem = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    font-size: 13px;
    color: #1e293b;
    line-height: 1.4;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: #f8fafc;
    }

    input {
        margin-top: 2px;
        flex-shrink: 0;
        cursor: pointer;
    }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
`;

const SavingOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
`;

const ModalEditarGrupo = ({ grupo, onSaved }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [permissoes, setPermissoes] = useState([]);
    const [grupoInfo, setGrupoInfo] = useState(grupo);

    const fetchPermissoes = useCallback(async () => {
        if (!grupo?.id) return;

        setLoading(true);

        try {
            const { data } = await api.get(LIST_PERMISSOES_URL, {
                params: { grupo_id: grupo.id },
            });

            setGrupoInfo(data?.grupo || grupo);
            setPermissoes(Array.isArray(data?.permissoes) ? data.permissoes : []);
        } catch (error) {
            console.error(error);
            setPermissoes([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar permissões do grupo.");
        } finally {
            setLoading(false);
        }
    }, [api, grupo, notifyError]);

    useEffect(() => {
        fetchPermissoes();
    }, [fetchPermissoes]);

    const handleClose = () => {
        closeModal(MODAL_ID);
    };

    const handleToggle = async (permissaoId) => {
        if (saving) return;

        const previous = permissoes;
        const current = permissoes.find((p) => Number(p.id) === Number(permissaoId));
        if (!current) return;

        const nextHas = current.has_permission === 1 ? 0 : 1;

        setPermissoes((prev) =>
            prev.map((p) =>
                Number(p.id) === Number(permissaoId) ? { ...p, has_permission: nextHas } : p
            )
        );

        setSaving(true);

        try {
            await api.post(SET_PERMISSAO_URL, {
                grupo_id: grupo.id,
                permissao_id: permissaoId,
                has_permission: nextHas,
            });
            onSaved?.();
        } catch (error) {
            console.error(error);
            setPermissoes(previous);
            notifyError(error?.response?.data?.message || "Erro ao atualizar permissão.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state changeContent={handleClose}>
            <Modal header close={handleClose} size="lg">
                <Container>
                    <div>
                        <Titulo>Editar permissões — {grupoInfo?.nome || grupo?.nome}</Titulo>
                        <Subtitulo>
                            Marque ou desmarque as permissões do seu módulo neste grupo.
                        </Subtitulo>
                    </div>

                    {saving && (
                        <SavingOverlay>
                            <SmallLoading />
                        </SavingOverlay>
                    )}

                    {loading ? (
                        <Vazio>Carregando permissões...</Vazio>
                    ) : permissoes.length === 0 ? (
                                <Vazio>Nenhuma permissão disponível para o seu módulo.</Vazio>
                    ) : (
                        <Lista>
                            {permissoes.map((item) => (
                                <PermissaoItem key={item.id}>
                                    <input
                                        type="checkbox"
                                        checked={item.has_permission === 1}
                                        disabled={saving}
                                        onChange={() => handleToggle(item.id)}
                                    />
                                    <span>{item.descricao}</span>
                                </PermissaoItem>
                            ))}
                        </Lista>
                    )}
                </Container>
            </Modal>
        </Overlay>
    );
};

export default ModalEditarGrupo;
